import { ApiError } from '../work/gateway.js';

/**
 * The browser's calls to the Stage 8 Finance endpoints.
 *
 * SAME DISCIPLINE AS STAGES 5, 6 AND 7, and the same three things it does not
 * do. Nothing here names a TENANT, because there is nothing it could name one
 * with — the server takes it from the session cookie and ignores anything a
 * request says about it. Nothing here FILTERS, counts or hides: what these
 * calls return has already been through `accessPredicate` in SQL. And nothing
 * here does ACCOUNTING: no total is computed, no tax is worked out, no balance
 * is derived. Every figure on a Finance screen was calculated by the server
 * over exact integers, and the browser's job is to render the string it was
 * given.
 *
 * MONEY IS A STRING OF MINOR UNITS, IN BOTH DIRECTIONS, and it stays a string
 * the whole way through this file. `JSON.parse` would turn a number into a
 * double and lose pennies above 2^53; formatting for display happens at the
 * last possible moment, in one function, and never feeds back into a request.
 *
 * Every unsafe call carries an `Idempotency-Key` generated HERE, for the Stage
 * 5 reason: a key the server minted would be a different key on every retry,
 * which is the same as having none.
 */

export interface LegalEntity {
  readonly id: string;
  readonly name: string;
}

export interface Invoice {
  readonly id: string;
  readonly legalEntityId: string;
  readonly partyId: string;
  readonly invoiceNumber: string | null;
  readonly status: string;
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly currency: string;
  readonly netTotal: string;
  readonly taxTotal: string;
  readonly grossTotal: string;
  readonly customerReference: string | null;
  readonly journalId: string | null;
  /** Why it is held out of collections right now, or null (FR-ARC-003). */
  readonly disputeReason: string | null;
  /**
   * FR-VAT-008. The statutory wording the document must show, and the VAT the
   * CUSTOMER must account for — `null` on an ordinary invoice, which says "no
   * reverse charge" rather than leaving the consumer to guess from an absent
   * field.
   */
  readonly reverseCharge: { readonly notice: string; readonly tax: string } | null;
  readonly version: number;
}

export interface InvoiceDispute {
  readonly id: string;
  readonly invoiceId: string;
  readonly legalEntityId: string;
  readonly reason: string;
  readonly raisedBy: string;
  readonly raisedAt: string;
  readonly resolution: string | null;
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | null;
}

export interface InvoiceLine {
  readonly id: string;
  readonly lineNo: number;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly netAmount: string;
  readonly taxCode: string | null;
  readonly taxAmount: string;
  readonly accountId: string;
}

export interface DraftLine {
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly taxCode?: string;
  readonly accountId: string;
}

/**
 * A bill line. FR-APB-002 routes a bill "by value, category and cost centre",
 * and one bill routinely covers two departments — so the cost centre belongs
 * to the LINE and not to the document. The server has accepted it since Stage
 * 8; nothing sent one.
 *
 * THE ENGINE CHECKS IT, NOT THIS. One table holds cost centres, departments,
 * projects and jobs, and the posting engine refuses a line whose dimension is
 * of the wrong kind. The browser offers the right ones; it does not enforce
 * them.
 */
export interface BillDraftLine extends DraftLine {
  readonly costCentreId?: string;
}

export interface Bill {
  readonly id: string;
  readonly legalEntityId: string;
  readonly partyId: string;
  readonly supplierReference: string;
  readonly status: string;
  readonly billDate: string;
  readonly dueDate: string | null;
  readonly currency: string;
  readonly netTotal: string;
  readonly taxTotal: string;
  /** FR-VAT-008. What this purchase makes us liable for, outside the gross. */
  readonly reverseChargeTax: string;
  readonly grossTotal: string;
  readonly journalId: string | null;
}

export interface DuplicateCandidate {
  readonly billId: string;
  readonly supplierReference: string;
  readonly billDate: string;
  readonly grossTotal: string;
  readonly basis: string;
}

export interface CreditNote {
  readonly id: string;
  readonly creditNoteNumber: string | null;
  readonly status: string;
  readonly issueDate: string | null;
  readonly currency: string;
  readonly grossTotal: string;
  readonly reasonCode: string;
  readonly journalId: string | null;
}

export interface SupplierCredit {
  readonly id: string;
  readonly supplierReference: string;
  readonly status: string;
  readonly creditDate: string;
  readonly currency: string;
  readonly grossTotal: string;
  readonly journalId: string | null;
}

/** A receipt or a payment: the same document from two sides. */
export interface SettlementDocument {
  readonly id: string;
  readonly partyId: string;
  readonly number: string | null;
  readonly status: string;
  readonly occurredOn: string;
  readonly currency: string;
  readonly amount: string;
  readonly allocated: string;
  /** Computed by the SERVER. The browser never subtracts money. */
  readonly remaining: string;
  readonly reference: string | null;
}

export interface AllocationResult {
  readonly allocationId: string;
  readonly sourceRemaining: string;
  readonly targetOutstanding: string;
  readonly targetStatus: string;
}

export interface AgeingBucket {
  readonly partyId: string;
  readonly bucket: string;
  readonly outstanding: string;
  readonly documents: number;
  /**
   * Debt held out of collections. It is still owed and still counted — the
   * server splits it rather than dropping it, and this column is why.
   */
  readonly disputed: boolean;
}

export interface Account {
  readonly id: string;
  readonly legalEntityId: string;
  readonly code: string;
  readonly name: string;
  readonly accountType: string;
  readonly controlKind: string | null;
  readonly isActive: boolean;
}

export interface FiscalYear {
  readonly id: string;
  readonly code: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly status: string;
}

export interface AccountingPeriod {
  readonly id: string;
  readonly fiscalYearId: string;
  readonly ordinal: number;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly status: string;
}

export interface Journal {
  readonly id: string;
  readonly journalNumber: string | null;
  readonly journalKind: string;
  readonly status: string;
  readonly postingDate: string;
  readonly description: string | null;
  readonly currency: string;
  readonly sourceType: string | null;
  readonly sourceId: string | null;
  readonly reversedById: string | null;
}

/**
 * ONE LINE OF A POSTED JOURNAL, AS THE SERVER ACTUALLY SENDS IT.
 *
 * **THIS DECLARATION WAS WRONG, AND THE JOURNAL PAGE CRASHED ON IT.** It said
 * `lineNo`, `debit` and `credit`; `GET /finance/journals/{id}` has always
 * returned `debitTxn`, `creditTxn`, `debitBase` and `creditBase` and no line
 * number at all. `formatMoney(undefined)` throws on its first `startsWith`, so
 * the screen went blank rather than showing a wrong figure — which is the only
 * mercy in it. A type that describes a payload nobody sends is not a type.
 *
 * TWO PAIRS, BECAUSE A POSTING HAS TWO CURRENCIES. `…Txn` is the document's own
 * currency and `…Base` is the entity's. They are equal on a domestic posting and
 * they are not on any other, so a screen states which one it is showing.
 */
export interface JournalLine {
  readonly accountId: string;
  readonly description: string | null;
  /** Minor units, in the journal's own currency. */
  readonly debitTxn: string;
  readonly creditTxn: string;
  /** Minor units, in the entity's base currency. */
  readonly debitBase: string;
  readonly creditBase: string;
  readonly partyId: string | null;
  readonly taxCode: string | null;
}

export interface LedgerEntry {
  readonly id: string;
  readonly journalId: string;
  readonly journalNumber: string | null;
  readonly accountCode: string;
  readonly accountName: string;
  readonly postingDate: string;
  readonly description: string | null;
  readonly debit: string;
  readonly credit: string;
  readonly sourceType: string | null;
}

export interface TrialBalance {
  readonly rows: readonly {
    readonly accountId: string;
    readonly accountCode: string;
    readonly accountName: string;
    readonly accountType: string;
    readonly debit: string;
    readonly credit: string;
    readonly balance: string;
  }[];
  /** THE SERVER'S ARITHMETIC. The screen renders `balanced`; it does not decide it. */
  readonly totals: {
    readonly debit: string;
    readonly credit: string;
    readonly balanced: boolean;
  };
}

export interface BankAccount {
  readonly id: string;
  readonly legalEntityId: string;
  readonly name: string;
  readonly accountKind: string;
  readonly currency: string;
  readonly accountLast4: string | null;
  readonly sortCodeLast4: string | null;
  readonly isActive: boolean;
}

export interface BankTransaction {
  readonly id: string;
  readonly bankAccountId: string;
  readonly transactedOn: string;
  readonly description: string;
  readonly reference: string | null;
  readonly amount: string;
  readonly currency: string;
  readonly matchState: string;
}

export interface ReconciliationMatch {
  readonly id: string;
  readonly bankTransactionId: string;
  readonly targetKind: string;
  readonly targetId: string;
  readonly amount: string;
  readonly matchKind: string;
  readonly confidence: string | null;
  /** Null means SUGGESTION. It settles nothing until a person accepts it. */
  readonly confirmedAt: string | null;
}

export interface Reconciliation {
  readonly id: string;
  readonly bankAccountId: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly statementBalance: string;
  readonly ledgerBalance: string;
  readonly status: string;
}

export interface VatPeriod {
  readonly id: string;
  readonly legalEntityId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly dueOn: string | null;
  readonly scheme: string;
  readonly status: string;
}

export interface VatBox {
  readonly boxNumber: number;
  readonly amount: string;
  readonly sourceCount: number;
}

export interface VatReturn {
  readonly id: string;
  readonly vatPeriodId: string;
  readonly status: string;
  readonly reconciledToLedger: boolean;
  readonly reconciliationDifference: string | null;
  readonly boxes: readonly VatBox[];
}

export interface VatBoxExplanation {
  readonly boxNumber: number;
  readonly amount: string;
  readonly sources: readonly {
    readonly sourceKind: string;
    readonly sourceId: string;
    readonly occurredOn: string;
    readonly reference: string | null;
    readonly net: string;
    readonly tax: string;
  }[];
  readonly adjustments: readonly {
    readonly id: string;
    readonly amount: string;
    readonly adjustmentKind: string;
    readonly reason: string;
  }[];
}

/**
 * ONE PARTY, ONE FINANCE RELATIONSHIP PER LEGAL ENTITY.
 *
 * `displayName` is the PARTY's name, joined in by the server — Finance holds
 * the terms and the coding, never the identity. Two records for one
 * organisation drift apart the first time somebody changes an address on one
 * of them, which is why there is only ever the one.
 *
 * EVERY FIELD BELOW IS ALREADY IN THE SERVER'S ANSWER. This interface used to
 * declare six of eleven columns `/finance/customers` has always returned, so
 * the browser was discarding the terms, the credit limit, the credit policy,
 * the billing currency and whether the relationship was still active. Reading
 * more of a payload that already arrives is not a new API.
 */
export interface FinanceCustomer {
  readonly partyId: string;
  readonly legalEntityId: string;
  readonly displayName: string;
  readonly paymentTermId: string | null;
  /** Minor units, as a string. The browser never does arithmetic on it. */
  readonly creditLimit: string | null;
  /** What the server does when the limit is passed: 'warn' or 'block'. */
  readonly creditPolicy: string;
  readonly isOnHold: boolean;
  readonly defaultIncomeAccountId: string | null;
  readonly defaultTaxCode: string | null;
  readonly billingCurrency: string | null;
  /** Where this customer's invoices are sent by default (0075). */
  readonly billingEmail: string | null;
  readonly isActive: boolean;
}

/** As `FinanceCustomer`. Suppliers carry no credit terms, and never a full
 * bank account number — the server returns the last four digits only. */
export interface FinanceSupplier {
  readonly partyId: string;
  readonly legalEntityId: string;
  readonly displayName: string;
  readonly paymentTermId: string | null;
  readonly defaultExpenseAccountId: string | null;
  readonly defaultTaxCode: string | null;
  readonly bankAccountLast4: string | null;
  readonly isActive: boolean;
}

/**
 * THE INVOICE DOCUMENT, AND EVERY ATTEMPT TO SEND IT.
 *
 * `base64` rather than a URL, because a route serving raw PDF bytes could only be
 * shown by pointing an `<iframe>` at it — and an iframe sends no credentials, so
 * the route would have to be reachable without a session. The bytes arrive inside
 * the authorised response and the browser makes a blob of them.
 */
export interface InvoiceDocument {
  readonly fileName: string;
  readonly contentType: string;
  readonly base64: string;
  readonly byteSize: number;
  readonly sha256: string;
}

/**
 * One attempt, as the history shows it.
 *
 * `state` HAS NO `delivered` MEMBER, and no screen may invent one. SMTP reports
 * that a server accepted a message, not that anybody received it — so `sent` is
 * described to the reader as handed to the mail server.
 */
export interface InvoiceDelivery {
  readonly id: string;
  readonly invoiceId: string;
  readonly legalEntityId: string;
  readonly channel: string;
  readonly recipient: string;
  readonly subject: string;
  readonly documentFileName: string;
  readonly documentByteSize: number;
  readonly documentSha256: string;
  readonly state: 'queued' | 'sent' | 'failed';
  readonly providerMessageId: string | null;
  readonly error: string | null;
  readonly requestedBy: string;
  readonly requestedByName: string | null;
  readonly requestedAt: string;
  readonly settledAt: string | null;
  readonly correlationId: string;
  /**
   * PRESENT ONLY IN THE REGISTER, where a row must name what it was about
   * without the reader already knowing. The per-invoice history omits both,
   * because the invoice is the page.
   */
  readonly invoiceNumber?: string | null;
  readonly customerName?: string | null;
}

/**
 * Screen 03's four KPI cards, as the server computed them.
 *
 * Minor-unit strings, summed in SQL. The browser formats them and does nothing
 * else with them — a screen that added these up would be doing accounting in a
 * language whose numbers are doubles.
 */
export interface CustomerSummary {
  readonly totalBilled: string;
  readonly outstanding: string;
  readonly overdue: string;
  readonly openInvoices: number;
  readonly oldestOverdueDays: number | null;
  readonly oldestOverdueNumber: string | null;
  readonly currency: string | null;
}

/** What a legal entity prints on the documents it sends out. */
export interface DocumentSettings {
  readonly legalEntityId: string;
  readonly tradingName: string | null;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postcode: string | null;
  readonly countryCode: string;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly paymentDetails: string | null;
  readonly defaultNotes: string | null;
  readonly defaultTerms: string | null;
}

export interface DocumentSettingsInput {
  legalEntityId: string;
  tradingName?: string;
  addressLine1: string;
  addressLine2?: string;
  city?: string;
  region?: string;
  postcode?: string;
  countryCode: string;
  contactEmail?: string;
  contactPhone?: string;
  paymentDetails?: string;
  defaultNotes?: string;
  defaultTerms?: string;
}

export interface PartyAddressInput {
  addressKind: 'registered' | 'billing' | 'shipping';
  line1: string;
  line2?: string;
  city?: string;
  region?: string;
  postcode?: string;
  countryCode: string;
  isDefault?: boolean;
}

/** A party's address, as Screen 03 shows and edits it. */
export interface PartyAddress {
  readonly id: string;
  readonly addressKind: 'registered' | 'billing' | 'shipping';
  readonly line1: string;
  readonly line2: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postcode: string | null;
  readonly countryCode: string;
  readonly isDefault: boolean;
}

export interface PartyIdentifier {
  readonly id: string;
  readonly identifierKind: 'vat_number' | 'company_number';
  readonly value: string;
}

// ---- Stage 8 gap closure: setup, schedules, rules and revaluation ------------

/** FR-SET-005. An entity's settings, including whether its books are open. */
export interface EntitySettings {
  readonly legalEntityId: string;
  readonly baseCurrency: string;
  readonly accountingBasis: string;
  readonly vatScheme: string;
  readonly lifecycle: string;
  readonly wentLiveAt: string | null;
  readonly fxGainLossAccountId: string | null;
  readonly unreconciledCloseMaxCount: number | null;
  readonly unreconciledCloseMaxAgeDays: number | null;
}

/**
 * FR-SET-005. The opening position, and whether it balances.
 *
 * `balanced` IS THE SERVER'S ANSWER, not a comparison this file makes. The two
 * totals are beside it so a reader can see what it is an answer about — but
 * the browser must not be the thing that decides whether a set of books adds
 * up, and a UI that compared the strings itself would be exactly that.
 */
export interface OpeningPosition {
  readonly legalEntityId: string;
  readonly lifecycle: string;
  readonly debit: string;
  readonly credit: string;
  readonly entries: number;
  readonly balanced: boolean;
}

/** FR-PRD-002. How one catalogue item is coded in one entity's books. */
export interface ItemDefaults {
  readonly itemId: string;
  readonly itemCode: string;
  readonly itemName: string;
  readonly incomeAccountId: string | null;
  readonly expenseAccountId: string | null;
  readonly defaultTaxCode: string | null;
  readonly defaultUnitPrice: string | null;
}

/** FR-LED-005. An accrual, a prepayment or a standing charge. */
export interface RecurringDefinition {
  readonly id: string;
  readonly legalEntityId: string;
  readonly name: string;
  readonly kind: string;
  readonly description: string;
  readonly currency: string;
  readonly frequency: string;
  readonly dayOfMonth: number;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly autoReverse: boolean;
  readonly isActive: boolean;
}

export interface RecurringOccurrence {
  readonly id: string;
  readonly occurrenceDate: string;
  readonly journalId: string | null;
  readonly reversalJournalId: string | null;
}

/** FR-REC-002. A tenant's own matching rule. It suggests; it never confirms. */
export interface MatchRule {
  readonly id: string;
  readonly legalEntityId: string | null;
  readonly name: string;
  readonly isActive: boolean;
  readonly priority: number;
  readonly direction: string;
  readonly narrativeContains: string | null;
  readonly windowDays: number;
  readonly amountTolerance: string;
  readonly amountToleranceBp: number;
  readonly confidence: string;
}

export interface RuleEffectiveness {
  readonly ruleId: string;
  readonly name: string;
  readonly suggested: number;
  readonly confirmed: number;
}

/** FR-REC-006. Bank lines nobody has reconciled, by age band. */
export interface UnreconciledBucket {
  readonly bankAccountId: string;
  readonly bankAccountName: string;
  readonly bucket: string;
  readonly lines: number;
  readonly amount: string;
  readonly oldestDays: number;
}

/** FR-CUR-003. One period-end restatement of foreign-currency balances. */
export interface Revaluation {
  readonly id: string;
  readonly legalEntityId: string;
  readonly asOf: string;
  readonly baseCurrency: string;
  readonly journalId: string | null;
  readonly reversalJournalId: string | null;
}

export interface RevaluationLine {
  readonly id: string;
  readonly accountCode: string;
  readonly currency: string;
  readonly txnBalance: string;
  readonly carryingBase: string;
  readonly rate: string;
  readonly rateSource: string;
  readonly restatedBase: string;
  readonly differenceBase: string;
}

/**
 * FR-LED-006. A cost centre, department, project or job.
 *
 * THE KIND IS CARRIED, not inferred from where the row was found. One table
 * holds all four, so a screen that showed a department under "Projects"
 * because of which list it was fetched into would be wrong the first time
 * somebody filtered the other way.
 */
export interface FinanceDimension {
  readonly id: string;
  readonly legalEntityId: string;
  readonly dimensionKind: string;
  readonly code: string;
  readonly name: string;
  readonly isActive: boolean;
}

/** FR-SET-006. The terms a due date is derived from — by the server. */
export interface FinancePaymentTerm {
  readonly id: string;
  readonly legalEntityId: string;
  readonly code: string;
  readonly name: string;
  readonly basis: string;
  readonly days: number;
  readonly isDefault: boolean;
  readonly isActive: boolean;
}

/** FR-RPT-001, FR-RPT-003. What Finance ships, installed or not. */
export interface FinanceReportCatalogueEntry {
  readonly key: string;
  readonly name: string;
  readonly description: string;
  readonly datasetKey: string;
}

export interface FinanceGateway {
  customers(legalEntityId?: string): Promise<readonly FinanceCustomer[]>;
  suppliers(legalEntityId?: string): Promise<readonly FinanceSupplier[]>;
  /**
   * Give a party a Finance relationship with this entity, or change the one it
   * has. UPSERT IS THE SERVER'S WORD FOR IT, and the reason there is one call
   * rather than a create and an update: a party either is a customer of this
   * entity or is not, and asking twice must not produce two of them.
   *
   * IT TAKES A `partyId`, WHICH IS THE WHOLE POINT. Identity is Party Master's
   * and stays there; this adds only what Finance owns.
   */
  saveCustomer(input: {
    legalEntityId: string;
    partyId: string;
    paymentTermId?: string;
    defaultIncomeAccountId?: string;
    defaultTaxCode?: string;
    billingCurrency?: string;
    billingEmail?: string;
  }): Promise<FinanceCustomer | null>;
  saveSupplier(input: {
    legalEntityId: string;
    partyId: string;
    paymentTermId?: string;
    defaultExpenseAccountId?: string;
    defaultTaxCode?: string;
  }): Promise<FinanceSupplier | null>;
  invoices(legalEntityId?: string): Promise<readonly Invoice[]>;
  invoice(id: string): Promise<{ invoice: Invoice; lines: readonly InvoiceLine[] }>;
  createInvoice(input: {
    legalEntityId: string;
    partyId: string;
    currency: string;
    customerReference?: string;
    lines: readonly DraftLine[];
  }): Promise<Invoice>;
  replaceInvoiceLines(id: string, lines: readonly DraftLine[]): Promise<Invoice>;
  issueInvoice(id: string, input: { issueDate?: string; dueInDays?: number }): Promise<Invoice>;
  customerSummary(partyId: string, legalEntityId: string): Promise<CustomerSummary>;
  /** Render this invoice as a PDF. A draft renders too, watermarked. */
  invoiceDocument(id: string): Promise<InvoiceDocument>;
  /** Ask for it to be emailed. Answers with a QUEUED attempt, never a sent one. */
  sendInvoice(
    id: string,
    input: { recipient?: string; subject?: string; message?: string },
  ): Promise<InvoiceDelivery>;
  invoiceDeliveries(id: string): Promise<readonly InvoiceDelivery[]>;
  /** Screen 33: everything sent out of one legal entity, newest first. */
  deliveryRegister(legalEntityId: string): Promise<readonly InvoiceDelivery[]>;
  documentSettings(legalEntityId: string): Promise<DocumentSettings | null>;
  saveDocumentSettings(input: DocumentSettingsInput): Promise<DocumentSettings | null>;
  partyDetails(
    partyId: string,
  ): Promise<{ addresses: readonly PartyAddress[]; identifiers: readonly PartyIdentifier[] }>;
  savePartyAddress(partyId: string, input: PartyAddressInput): Promise<readonly PartyAddress[]>;
  invoiceDisputes(id: string): Promise<readonly InvoiceDispute[]>;
  disputeInvoice(id: string, input: { reason: string }): Promise<InvoiceDispute>;
  resolveInvoiceDispute(id: string, input: { resolution: string }): Promise<Invoice>;
  creditNotes(legalEntityId?: string): Promise<readonly CreditNote[]>;
  issueCreditNote(input: {
    legalEntityId: string;
    partyId: string;
    currency: string;
    againstInvoiceId?: string;
    reasonCode: string;
    lines: readonly DraftLine[];
  }): Promise<{ creditNoteId: string; journalId: string }>;
  receipts(legalEntityId?: string): Promise<readonly SettlementDocument[]>;
  recordReceipt(input: {
    legalEntityId: string;
    partyId: string;
    currency: string;
    amount: string;
    receivedOn?: string;
    reference?: string;
  }): Promise<{ receiptId: string }>;
  allocateReceivable(input: {
    sourceKind: 'receipt' | 'credit_note';
    sourceId: string;
    targetId: string;
    amount: string;
  }): Promise<AllocationResult>;
  receivablesAgeing(asOf?: string): Promise<readonly AgeingBucket[]>;

  bills(legalEntityId?: string): Promise<readonly Bill[]>;
  bill(id: string): Promise<{ bill: Bill; lines: readonly InvoiceLine[] }>;
  enterBill(input: {
    legalEntityId: string;
    partyId: string;
    supplierReference: string;
    billDate: string;
    currency: string;
    lines: readonly BillDraftLine[];
  }): Promise<{ bill: Bill; possibleDuplicates: readonly DuplicateCandidate[] }>;
  /**
   * FR-APB-003. Send a bill down the tenant's own approval route.
   *
   * `routed: false` means the tenant has published no route for bills — the
   * document stays a draft and is approved by permission, as before. Reported
   * rather than hidden: "nothing happened because there is no workflow" and
   * "it is waiting for somebody" are different facts.
   */
  submitBill(id: string): Promise<{ bill: Bill; routed: boolean }>;
  approveBill(id: string): Promise<Bill>;
  postBill(id: string): Promise<{ journalId: string }>;
  supplierCredits(legalEntityId?: string): Promise<readonly SupplierCredit[]>;
  recordSupplierCredit(input: {
    legalEntityId: string;
    partyId: string;
    supplierReference: string;
    creditDate: string;
    currency: string;
    lines: readonly {
      description: string;
      netAmount: string;
      taxCode?: string;
      accountId: string;
    }[];
  }): Promise<{ supplierCreditId: string }>;
  payments(legalEntityId?: string): Promise<readonly SettlementDocument[]>;
  recordPayment(input: {
    legalEntityId: string;
    partyId: string;
    currency: string;
    amount: string;
    paidOn?: string;
  }): Promise<{ paymentId: string }>;
  allocatePayable(input: {
    sourceKind: 'payment' | 'supplier_credit';
    sourceId: string;
    targetId: string;
    amount: string;
  }): Promise<AllocationResult>;
  payablesAgeing(asOf?: string): Promise<readonly AgeingBucket[]>;

  accounts(legalEntityId?: string): Promise<readonly Account[]>;
  fiscalYears(legalEntityId: string): Promise<readonly FiscalYear[]>;
  /**
   * FR-SET-001. Opening next year's calendar, which until now could only be
   * done with database access. The browser sends a STRUCTURE, never a list of
   * dates it worked out: the server derives the periods and checks that they
   * tile the year, and a calendar computed here would be a second opinion
   * about which month a posting falls in.
   */
  openFiscalYear(input: {
    legalEntityId: string;
    code: string;
    startsOn: string;
    structure: 'monthly' | 'quarterly';
  }): Promise<{ fiscalYear: FiscalYear; periods: number }>;
  closeFiscalYear(id: string): Promise<FiscalYear>;
  periods(legalEntityId: string): Promise<readonly AccountingPeriod[]>;
  // ---- Stage 8 gap closure -------------------------------------------------
  entitySettings(): Promise<readonly EntitySettings[]>;
  openingPosition(legalEntityId: string): Promise<OpeningPosition>;
  goLive(legalEntityId: string): Promise<void>;
  itemDefaults(legalEntityId: string): Promise<readonly ItemDefaults[]>;
  setItemDefaults(
    legalEntityId: string,
    input: {
      itemId: string;
      incomeAccountId?: string;
      expenseAccountId?: string;
      defaultTaxCode?: string;
      defaultUnitPrice?: string;
    },
  ): Promise<void>;
  setFxAccount(input: { legalEntityId: string; accountId: string }): Promise<void>;
  revaluations(legalEntityId: string): Promise<readonly Revaluation[]>;
  revalue(legalEntityId: string, asOf: string): Promise<{ created: boolean; difference: string }>;
  revaluationLines(revaluationId: string): Promise<readonly RevaluationLine[]>;
  recurring(legalEntityId: string): Promise<readonly RecurringDefinition[]>;
  recurringOccurrences(id: string): Promise<readonly RecurringOccurrence[]>;
  /** FR-LED-005. Stop a schedule, or start it again. Nothing deletes one. */
  setRecurringActive(id: string, isActive: boolean): Promise<void>;
  generateOccurrence(id: string, occurrenceDate: string): Promise<{ created: boolean }>;
  matchRules(legalEntityId?: string): Promise<readonly MatchRule[]>;
  saveMatchRule(input: {
    legalEntityId?: string;
    name: string;
    direction?: string;
    narrativeContains?: string;
    windowDays?: number;
    amountTolerance?: string;
    amountToleranceBp?: number;
    confidence?: number;
    priority?: number;
  }): Promise<MatchRule>;
  ruleEffectiveness(legalEntityId: string): Promise<readonly RuleEffectiveness[]>;
  unreconciledAgeing(legalEntityId: string): Promise<readonly UnreconciledBucket[]>;
  dimensions(legalEntityId: string): Promise<readonly FinanceDimension[]>;
  saveDimension(input: {
    legalEntityId: string;
    dimensionKind: 'cost_centre' | 'department' | 'project' | 'job';
    code: string;
    name: string;
    isActive?: boolean;
  }): Promise<FinanceDimension>;
  paymentTerms(legalEntityId: string): Promise<readonly FinancePaymentTerm[]>;
  savePaymentTerm(input: {
    legalEntityId: string;
    code: string;
    name: string;
    basis: 'days_after_invoice' | 'end_of_month_plus_days';
    days: number;
    isDefault?: boolean;
    isActive?: boolean;
  }): Promise<FinancePaymentTerm>;
  financeReportCatalogue(): Promise<readonly FinanceReportCatalogueEntry[]>;
  installFinanceReports(): Promise<readonly { key: string; outcome: string }[]>;
  setPeriodStatus(id: string, status: 'open' | 'closed' | 'locked'): Promise<void>;

  journals(legalEntityId?: string): Promise<readonly Journal[]>;
  journal(id: string): Promise<{ journal: Journal; lines: readonly JournalLine[] }>;
  ledgerEntries(filter: {
    legalEntityId?: string;
    accountId?: string;
    from?: string;
    to?: string;
  }): Promise<readonly LedgerEntry[]>;
  trialBalance(legalEntityId: string, asOf?: string): Promise<TrialBalance>;

  bankAccounts(legalEntityId?: string): Promise<readonly BankAccount[]>;
  unmatched(bankAccountId: string): Promise<readonly BankTransaction[]>;
  matches(bankTransactionId: string): Promise<readonly ReconciliationMatch[]>;
  suggest(bankTransactionId: string): Promise<readonly ReconciliationMatch[]>;
  acceptSuggestion(matchId: string): Promise<ReconciliationMatch>;
  unmatch(matchId: string, reason: string): Promise<void>;
  reconciliations(bankAccountId: string): Promise<readonly Reconciliation[]>;
  importStatement(input: {
    bankAccountId: string;
    fromDate: string;
    toDate: string;
    openingBalance: string;
    closingBalance: string;
    importFingerprint: string;
    lines: readonly {
      transactedOn: string;
      description: string;
      amount: string;
      externalId?: string;
    }[];
  }): Promise<{ statementId: string; imported: boolean; linesInserted: number }>;

  vatPeriods(legalEntityId: string): Promise<readonly VatPeriod[]>;
  prepareVatReturn(periodId: string): Promise<VatReturn>;
  vatReturn(id: string): Promise<VatReturn>;
  explainVatBox(returnId: string, boxNumber: number): Promise<VatBoxExplanation>;
  submitVatReturn(id: string): Promise<VatReturn>;
}

function newKey(): string {
  return crypto.randomUUID();
}

export function createFinanceGateway(baseUrl = '/api/v1'): FinanceGateway {
  async function call<T>(path: string, init?: RequestInit & { readonly key?: string }): Promise<T> {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (init?.key !== undefined) headers['idempotency-key'] = init.key;

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) {
      // RFC 7807 from the API, read rather than guessed. A 404 and a 403 mean
      // different things and the SERVER decided which this was; the browser
      // repeating that decision would undo the concealment the ordering exists
      // to provide.
      const problem = (await response.json().catch(() => ({}))) as {
        title?: string;
        detail?: string;
      };
      throw new ApiError(response.status, problem.title ?? 'Something went wrong', problem.detail);
    }
    return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
  }

  const post = <T>(path: string, body: unknown): Promise<T> =>
    call<T>(path, { method: 'POST', key: newKey(), body: JSON.stringify(body) });

  function scoped(path: string, legalEntityId?: string): string {
    return legalEntityId === undefined
      ? path
      : `${path}${path.includes('?') ? '&' : '?'}legalEntityId=${encodeURIComponent(legalEntityId)}`;
  }

  return {
    async customers(legalEntityId) {
      const body = await call<{ customers: readonly FinanceCustomer[] }>(
        scoped('/finance/customers', legalEntityId),
      );
      return body.customers;
    },
    async suppliers(legalEntityId) {
      const body = await call<{ suppliers: readonly FinanceSupplier[] }>(
        scoped('/finance/suppliers', legalEntityId),
      );
      return body.suppliers;
    },
    async saveCustomer(input) {
      // The server returns what it stored, read back through the caller's own
      // predicate. `null` therefore means "saved, and not yours to read back" —
      // which is not the same as "not saved", so it is passed on rather than
      // turned into an error here.
      const body = await post<{ customer: FinanceCustomer | null }>('/finance/customers', input);
      return body.customer;
    },
    async saveSupplier(input) {
      const body = await post<{ supplier: FinanceSupplier | null }>('/finance/suppliers', input);
      return body.supplier;
    },
    async invoices(legalEntityId) {
      const body = await call<{ invoices: readonly Invoice[] }>(
        scoped('/finance/invoices?limit=200', legalEntityId),
      );
      return body.invoices;
    },
    async invoice(id) {
      return call<{ invoice: Invoice; lines: readonly InvoiceLine[] }>(
        `/finance/invoices/${encodeURIComponent(id)}`,
      );
    },
    async createInvoice(input) {
      const body = await post<{ invoice: Invoice }>('/finance/invoices', input);
      return body.invoice;
    },
    async replaceInvoiceLines(id, lines) {
      const body = await call<{ invoice: Invoice }>(
        `/finance/invoices/${encodeURIComponent(id)}/lines`,
        { method: 'PUT', key: newKey(), body: JSON.stringify({ lines }) },
      );
      return body.invoice;
    },
    async issueInvoice(id, input) {
      const body = await post<{ invoice: Invoice }>(
        `/finance/invoices/${encodeURIComponent(id)}/issue`,
        input,
      );
      return body.invoice;
    },
    async customerSummary(partyId, legalEntityId) {
      const body = await call<{ summary: CustomerSummary }>(
        scoped(`/finance/customers/${encodeURIComponent(partyId)}/summary`, legalEntityId),
      );
      return body.summary;
    },
    async invoiceDocument(id) {
      const body = await call<{ document: InvoiceDocument }>(
        `/finance/invoices/${encodeURIComponent(id)}/document`,
      );
      return body.document;
    },
    async sendInvoice(id, input) {
      const body = await post<{ delivery: InvoiceDelivery }>(
        `/finance/invoices/${encodeURIComponent(id)}/send`,
        input,
      );
      return body.delivery;
    },
    async invoiceDeliveries(id) {
      const body = await call<{ deliveries: readonly InvoiceDelivery[] }>(
        `/finance/invoices/${encodeURIComponent(id)}/deliveries`,
      );
      return body.deliveries;
    },
    async deliveryRegister(legalEntityId) {
      const body = await call<{ deliveries: readonly InvoiceDelivery[] }>(
        scoped('/finance/deliveries', legalEntityId),
      );
      return body.deliveries;
    },
    async documentSettings(legalEntityId) {
      const body = await call<{ settings: DocumentSettings | null }>(
        `/finance/entities/${encodeURIComponent(legalEntityId)}/document-settings`,
      );
      return body.settings;
    },
    async saveDocumentSettings(input) {
      const { legalEntityId, ...rest } = input;
      const body = await call<{ settings: DocumentSettings | null }>(
        `/finance/entities/${encodeURIComponent(legalEntityId)}/document-settings`,
        { method: 'PUT', key: newKey(), body: JSON.stringify(rest) },
      );
      return body.settings;
    },
    async partyDetails(partyId) {
      return call<{
        addresses: readonly PartyAddress[];
        identifiers: readonly PartyIdentifier[];
      }>(`/parties/${encodeURIComponent(partyId)}/details`);
    },
    async savePartyAddress(partyId, input) {
      const body = await post<{ addresses: readonly PartyAddress[] }>(
        `/parties/${encodeURIComponent(partyId)}/addresses`,
        input,
      );
      return body.addresses;
    },
    async invoiceDisputes(id) {
      const body = await call<{ disputes: readonly InvoiceDispute[] }>(
        `/finance/invoices/${encodeURIComponent(id)}/disputes`,
      );
      return body.disputes;
    },
    async disputeInvoice(id, input) {
      const body = await post<{ dispute: InvoiceDispute }>(
        `/finance/invoices/${encodeURIComponent(id)}/disputes`,
        input,
      );
      return body.dispute;
    },
    async resolveInvoiceDispute(id, input) {
      const body = await post<{ invoice: Invoice }>(
        `/finance/invoices/${encodeURIComponent(id)}/disputes/resolve`,
        input,
      );
      return body.invoice;
    },
    async creditNotes(legalEntityId) {
      const body = await call<{ creditNotes: readonly CreditNote[] }>(
        scoped('/finance/credit-notes', legalEntityId),
      );
      return body.creditNotes;
    },
    async issueCreditNote(input) {
      return post<{ creditNoteId: string; journalId: string }>('/finance/credit-notes', input);
    },
    async receipts(legalEntityId) {
      const body = await call<{ receipts: readonly SettlementDocument[] }>(
        scoped('/finance/receipts', legalEntityId),
      );
      return body.receipts;
    },
    async recordReceipt(input) {
      return post<{ receiptId: string }>('/finance/receipts', input);
    },
    async allocateReceivable(input) {
      return post<AllocationResult>('/finance/receivables/allocations', input);
    },
    async receivablesAgeing(asOf) {
      const body = await call<{ buckets: readonly AgeingBucket[] }>(
        `/finance/receivables/ageing${asOf === undefined ? '' : `?asOf=${asOf}`}`,
      );
      return body.buckets;
    },

    async bills(legalEntityId) {
      const body = await call<{ bills: readonly Bill[] }>(
        scoped('/finance/bills?limit=200', legalEntityId),
      );
      return body.bills;
    },
    async bill(id) {
      return call<{ bill: Bill; lines: readonly InvoiceLine[] }>(
        `/finance/bills/${encodeURIComponent(id)}`,
      );
    },
    async enterBill(input) {
      return post<{ bill: Bill; possibleDuplicates: readonly DuplicateCandidate[] }>(
        '/finance/bills',
        input,
      );
    },
    async submitBill(id) {
      return post<{ bill: Bill; routed: boolean }>(
        `/finance/bills/${encodeURIComponent(id)}/submit`,
        {},
      );
    },
    async approveBill(id) {
      const body = await post<{ bill: Bill }>(
        `/finance/bills/${encodeURIComponent(id)}/approve`,
        {},
      );
      return body.bill;
    },
    async postBill(id) {
      return post<{ journalId: string }>(`/finance/bills/${encodeURIComponent(id)}/post`, {});
    },
    async supplierCredits(legalEntityId) {
      const body = await call<{ supplierCredits: readonly SupplierCredit[] }>(
        scoped('/finance/supplier-credits', legalEntityId),
      );
      return body.supplierCredits;
    },
    async recordSupplierCredit(input) {
      return post<{ supplierCreditId: string }>('/finance/supplier-credits', input);
    },
    async payments(legalEntityId) {
      const body = await call<{ payments: readonly SettlementDocument[] }>(
        scoped('/finance/payments', legalEntityId),
      );
      return body.payments;
    },
    async recordPayment(input) {
      return post<{ paymentId: string }>('/finance/payments', input);
    },
    async allocatePayable(input) {
      return post<AllocationResult>('/finance/payables/allocations', input);
    },
    async payablesAgeing(asOf) {
      const body = await call<{ buckets: readonly AgeingBucket[] }>(
        `/finance/payables/ageing${asOf === undefined ? '' : `?asOf=${asOf}`}`,
      );
      return body.buckets;
    },

    async accounts(legalEntityId) {
      const body = await call<{ accounts: readonly Account[] }>(
        scoped('/finance/accounts', legalEntityId),
      );
      return body.accounts;
    },
    async openFiscalYear(input) {
      return post<{ fiscalYear: FiscalYear; periods: number }>('/finance/fiscal-years', input);
    },
    async closeFiscalYear(id) {
      const body = await post<{ fiscalYear: FiscalYear }>(
        `/finance/fiscal-years/${encodeURIComponent(id)}/close`,
        {},
      );
      return body.fiscalYear;
    },
    async fiscalYears(legalEntityId) {
      const body = await call<{ fiscalYears: readonly FiscalYear[] }>(
        `/finance/fiscal-years?legalEntityId=${encodeURIComponent(legalEntityId)}`,
      );
      return body.fiscalYears;
    },
    async periods(legalEntityId) {
      const body = await call<{ periods: readonly AccountingPeriod[] }>(
        `/finance/periods?legalEntityId=${encodeURIComponent(legalEntityId)}`,
      );
      return body.periods;
    },
    async entitySettings() {
      const body = await call<{ entities: readonly EntitySettings[] }>('/finance/entities');
      return body.entities;
    },
    async openingPosition(legalEntityId) {
      return call<OpeningPosition>(
        `/finance/entities/${encodeURIComponent(legalEntityId)}/opening-balance`,
      );
    },
    async goLive(legalEntityId) {
      await post<unknown>(`/finance/entities/${encodeURIComponent(legalEntityId)}/go-live`, {});
    },
    async itemDefaults(legalEntityId) {
      const body = await call<{ items: readonly ItemDefaults[] }>(
        scoped('/finance/item-defaults', legalEntityId),
      );
      return body.items;
    },
    async setItemDefaults(legalEntityId, input) {
      await post<unknown>(scoped('/finance/item-defaults', legalEntityId), input);
    },
    async setFxAccount(input) {
      await post<unknown>('/finance/fx-account', input);
    },
    async revaluations(legalEntityId) {
      const body = await call<{ revaluations: readonly Revaluation[] }>(
        `/finance/entities/${encodeURIComponent(legalEntityId)}/revaluations`,
      );
      return body.revaluations;
    },
    async revalue(legalEntityId, asOf) {
      return post<{ created: boolean; difference: string }>(
        `/finance/entities/${encodeURIComponent(legalEntityId)}/revaluations`,
        { asOf },
      );
    },
    async revaluationLines(revaluationId) {
      const body = await call<{ lines: readonly RevaluationLine[] }>(
        `/finance/revaluations/${encodeURIComponent(revaluationId)}/lines`,
      );
      return body.lines;
    },
    async recurring(legalEntityId) {
      const body = await call<{ recurring: readonly RecurringDefinition[] }>(
        scoped('/finance/recurring', legalEntityId),
      );
      return body.recurring;
    },
    async recurringOccurrences(id) {
      const body = await call<{ occurrences: readonly RecurringOccurrence[] }>(
        `/finance/recurring/${encodeURIComponent(id)}/occurrences`,
      );
      return body.occurrences;
    },
    async setRecurringActive(id, isActive) {
      await post(`/finance/recurring/${encodeURIComponent(id)}/active`, { isActive });
    },
    async generateOccurrence(id, occurrenceDate) {
      return post<{ created: boolean }>(
        `/finance/recurring/${encodeURIComponent(id)}/occurrences`,
        { occurrenceDate },
      );
    },
    async matchRules(legalEntityId) {
      const body = await call<{ rules: readonly MatchRule[] }>(
        scoped('/finance/match-rules', legalEntityId),
      );
      return body.rules;
    },
    async saveMatchRule(input) {
      const body = await post<{ rule: MatchRule }>('/finance/match-rules', input);
      return body.rule;
    },
    async ruleEffectiveness(legalEntityId) {
      const body = await call<{ rules: readonly RuleEffectiveness[] }>(
        scoped('/finance/match-rules/effectiveness', legalEntityId),
      );
      return body.rules;
    },
    async unreconciledAgeing(legalEntityId) {
      const body = await call<{ buckets: readonly UnreconciledBucket[] }>(
        scoped('/finance/unreconciled-ageing', legalEntityId),
      );
      return body.buckets;
    },
    async dimensions(legalEntityId) {
      const body = await call<{ dimensions: readonly FinanceDimension[] }>(
        scoped('/finance/dimensions', legalEntityId),
      );
      return body.dimensions;
    },
    async saveDimension(input) {
      const body = await post<{ dimension: FinanceDimension }>('/finance/dimensions', input);
      return body.dimension;
    },
    async paymentTerms(legalEntityId) {
      const body = await call<{ paymentTerms: readonly FinancePaymentTerm[] }>(
        scoped('/finance/payment-terms', legalEntityId),
      );
      return body.paymentTerms;
    },
    async savePaymentTerm(input) {
      const body = await post<{ paymentTerm: FinancePaymentTerm }>('/finance/payment-terms', input);
      return body.paymentTerm;
    },
    async financeReportCatalogue() {
      const body = await call<{ reports: readonly FinanceReportCatalogueEntry[] }>(
        '/finance/reports/catalogue',
      );
      return body.reports;
    },
    async installFinanceReports() {
      const body = await post<{ reports: readonly { key: string; outcome: string }[] }>(
        '/finance/reports/install',
        {},
      );
      return body.reports;
    },
    async setPeriodStatus(id, status) {
      await post(`/finance/periods/${encodeURIComponent(id)}/status`, { status });
    },
    async journals(legalEntityId) {
      const body = await call<{ journals: readonly Journal[] }>(
        scoped('/finance/journals', legalEntityId),
      );
      return body.journals;
    },
    async journal(id) {
      return call<{ journal: Journal; lines: readonly JournalLine[] }>(
        `/finance/journals/${encodeURIComponent(id)}`,
      );
    },
    async ledgerEntries(filter) {
      const query = new URLSearchParams({ limit: '500' });
      if (filter.legalEntityId !== undefined) query.set('legalEntityId', filter.legalEntityId);
      if (filter.accountId !== undefined) query.set('accountId', filter.accountId);
      if (filter.from !== undefined) query.set('from', filter.from);
      if (filter.to !== undefined) query.set('to', filter.to);
      const body = await call<{ entries: readonly LedgerEntry[] }>(
        `/finance/ledger-entries?${query.toString()}`,
      );
      return body.entries;
    },
    async trialBalance(legalEntityId, asOf) {
      const query = new URLSearchParams({ legalEntityId });
      if (asOf !== undefined) query.set('to', asOf);
      return call<TrialBalance>(`/finance/trial-balance?${query.toString()}`);
    },

    async bankAccounts(legalEntityId) {
      const body = await call<{ bankAccounts: readonly BankAccount[] }>(
        scoped('/finance/bank-accounts', legalEntityId),
      );
      return body.bankAccounts;
    },
    async unmatched(bankAccountId) {
      const body = await call<{ transactions: readonly BankTransaction[] }>(
        `/finance/bank-accounts/${encodeURIComponent(bankAccountId)}/unmatched`,
      );
      return body.transactions;
    },
    async matches(bankTransactionId) {
      const body = await call<{ matches: readonly ReconciliationMatch[] }>(
        `/finance/bank-transactions/${encodeURIComponent(bankTransactionId)}/suggestions`,
      );
      return body.matches;
    },
    async suggest(bankTransactionId) {
      const body = await post<{ suggestions: readonly ReconciliationMatch[] }>(
        `/finance/bank-transactions/${encodeURIComponent(bankTransactionId)}/suggestions`,
        {},
      );
      return body.suggestions;
    },
    async acceptSuggestion(matchId) {
      const body = await post<{ match: ReconciliationMatch }>(
        `/finance/bank-matches/${encodeURIComponent(matchId)}/accept`,
        {},
      );
      return body.match;
    },
    async unmatch(matchId, reason) {
      await post(`/finance/bank-matches/${encodeURIComponent(matchId)}/unmatch`, { reason });
    },
    async reconciliations(bankAccountId) {
      const body = await call<{ reconciliations: readonly Reconciliation[] }>(
        `/finance/bank-accounts/${encodeURIComponent(bankAccountId)}/reconciliations`,
      );
      return body.reconciliations;
    },
    async importStatement(input) {
      return post<{ statementId: string; imported: boolean; linesInserted: number }>(
        '/finance/bank-statements',
        input,
      );
    },

    async vatPeriods(legalEntityId) {
      const body = await call<{ vatPeriods: readonly VatPeriod[] }>(
        `/finance/vat-periods?legalEntityId=${encodeURIComponent(legalEntityId)}`,
      );
      return body.vatPeriods;
    },
    async prepareVatReturn(periodId) {
      const body = await post<{ vatReturn: VatReturn }>(
        `/finance/vat-periods/${encodeURIComponent(periodId)}/prepare`,
        {},
      );
      return body.vatReturn;
    },
    async vatReturn(id) {
      const body = await call<{ vatReturn: VatReturn }>(
        `/finance/vat-returns/${encodeURIComponent(id)}`,
      );
      return body.vatReturn;
    },
    async explainVatBox(returnId, boxNumber) {
      return call<VatBoxExplanation>(
        `/finance/vat-returns/${encodeURIComponent(returnId)}/boxes/${String(boxNumber)}`,
      );
    },
    async submitVatReturn(id) {
      const body = await post<{ vatReturn: VatReturn }>(
        `/finance/vat-returns/${encodeURIComponent(id)}/submit`,
        {},
      );
      return body.vatReturn;
    },
  };
}
